/**
 * The authenticated home: the member's OWN dashboard, or the invitation to pick
 * one.
 *
 * IT IS A POINTER, NOT A PAGE. The home holds one dashboard id per member and
 * the shipped dashboard page draws it — the same read, the same run and the
 * same tiles the shared page shows, under the home frame instead of the shared
 * one. So the route's whole job is which of seven other renderings stands in
 * front of that page, and in what order: no org context; the grants not yet in
 * hand, which WAITS rather than judging, because `usePermissions()` reads
 * deny-all until its envelope lands and this is the route every cold load
 * arrives on; no `reports:run`, where the pointer read never fires and the
 * member is told which permission is missing rather than shown an empty home;
 * the read still out; the read FAILED, which is a connection fault and not an
 * empty home, so it takes the header's own line and offers a retry; a pointer
 * that names nothing; and a pointer whose dashboard has since been archived,
 * which is named so the member knows what happened.
 *
 * COMPARE IS ON BY DEFAULT HERE and nowhere else — `home-filters` reads an
 * absent `compare` as Previous period and spells `none` for a reader who turns
 * it off. The filters are the URL's as they are on the shared page, every write
 * replaces, and nothing is saved.
 *
 * ARCHIVING THE HOME FROM THE HOME NAVIGATES NOWHERE: the page's after-delete
 * exit invalidates the pointer, the re-read comes back archived, and this route
 * re-renders as that state on the page the reader was already on.
 */

import { useMemo, type JSX } from "react";
import { createFileRoute, useNavigate, type SearchSchemaInput } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { reportingKeys } from "@njbsoft/api-client";
import { DashboardIcon } from "@/components/icons";
import { EmptyState, PageHeader, PageSkeleton } from "@/components/ui";
import { DashboardPage } from "@/components/reporting/dashboards/dashboard-page";
import {
  HOME_DEFAULT_FILTERS,
  homeFiltersFromSearch,
  homeSearchFromFilters,
  parseHomeSearch,
  type HomeSearch,
} from "@/components/reporting/dashboards/home-filters";
import { useMyHomeDashboard } from "@/hooks/use-member-home-dashboard";
import { usePermissions } from "@/hooks/use-permissions";
import { useAppStore } from "@/store";

/**
 * `SearchSchemaInput` keeps the INPUT half optional while the output stays the
 * validated set, so every `to: "/"` — the sidebar's Home, the set-home toast's
 * way out, the login round trip — navigates without spelling filters it has no
 * opinion about.
 */
type HomeSearchInput = Record<string, unknown> & SearchSchemaInput;

export const Route = createFileRoute("/_authenticated/")({
  component: HomeRoute,
  validateSearch: (search: HomeSearchInput): HomeSearch => parseHomeSearch(search),
});

function HomeRoute(): JSX.Element {
  const search = Route.useSearch();
  const { t } = useTranslation("reporting");
  const { t: tOrg } = useTranslation("org");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const orgId = useAppStore((s) => s.activeOrgId);
  const { can, isLoading: permissionsLoading } = usePermissions();
  const pointer = useMyHomeDashboard(orgId);

  // Held on the search object: the page keys its injected instant — and through
  // it the whole run's overrides — on this object's identity, so a fresh one
  // per render would re-derive the run on every unrelated render.
  const filters = useMemo(() => homeFiltersFromSearch(search), [search]);

  const toDashboards = () => {
    void navigate({ to: "/reports", search: { tab: "dashboards" } });
  };

  /** The home's header, which every state but the dashboard itself stands under. */
  const framed = (title: string, body: JSX.Element): JSX.Element => (
    <div className="p-8">
      {/* A label, not a control — this is the home saying so. */}
      <p className="mb-2 text-sm text-muted">{t("home.eyebrow")}</p>
      <PageHeader title={title} />
      {body}
    </div>
  );

  /** Nothing is in hand yet — neither the grants nor the pointer. */
  const waiting = (
    <div className="p-8">
      <PageSkeleton />
    </div>
  );

  if (!orgId) {
    return (
      <div className="p-8">
        <p className="text-muted">{tOrg("noOrgContext")}</p>
      </div>
    );
  }

  // `usePermissions()` reads deny-all until its envelope resolves, so the grant
  // check below has to wait for a real answer — deciding earlier tells every
  // member, on every cold load of the app's own front door, that they lack a
  // permission they hold. The pointer read waits with it: the hook's gate is
  // the same unresolved `can`.
  if (permissionsLoading) return waiting;

  // The grant the pointer's own two operations are gated on. The hook holds the
  // same gate, so nothing is asked of the server here; what this decides is
  // which sentence a member without it reads.
  if (!can("reports", "run")) {
    return framed(
      t("home.empty.title"),
      <EmptyState
        icon={<DashboardIcon />}
        title={t("home.empty.pick")}
        description={t("home.empty.needsRun")}
      />,
    );
  }

  const home = pointer.data;
  if (home === undefined) {
    if (!pointer.isError) return waiting;
    // The failure is the headline: a home that says "No home dashboard yet" in
    // its largest line while the sentence under it says otherwise reads as the
    // empty home this state exists to distinguish itself from.
    return framed(
      t("home.loadFailed.title"),
      <EmptyState
        icon={<DashboardIcon />}
        title={t("home.loadFailed.body")}
        actionLabel={t("actions.retry")}
        onAction={() => {
          void pointer.refetch();
        }}
      />,
    );
  }

  // Nothing to draw, for one of two reasons: no dashboard was ever picked, or
  // the one that was has been archived since. One frame and one way on; only
  // the sentence differs.
  if (home.dashboard_id === null || home.archived) {
    return framed(
      t("home.empty.title"),
      <EmptyState
        icon={<DashboardIcon />}
        title={
          home.dashboard_id === null
            ? t("home.empty.pick")
            : // `name` is null only when `dashboard_id` is, which the branch
              // above already answered.
              t("home.empty.archived", { name: home.name ?? "" })
        }
        description={t("home.empty.how")}
        actionLabel={t("home.empty.action")}
        onAction={toDashboards}
      />,
    );
  }

  return (
    <div className="p-8">
      <DashboardPage
        orgId={orgId}
        dashboardId={home.dashboard_id}
        filters={filters}
        onFiltersChange={(next) => {
          void navigate({
            to: "/",
            // Spread because TypeScript grants an implicit index signature —
            // which is what the router's search parameter asks for — to
            // anonymous object types but not to interfaces, and `HomeSearch`
            // is built on one.
            search: { ...homeSearchFromFilters(next) },
            replace: true,
          });
        }}
        filterDefaults={HOME_DEFAULT_FILTERS}
        surface={{ kind: "home", onChangeHome: toDashboards }}
        onDeleted={() => {
          // No navigation: the re-read comes back archived and this route
          // renders that state where the reader already is.
          void queryClient.invalidateQueries({ queryKey: reportingKeys.home(orgId) });
        }}
        onOpenReport={(reportId) => {
          void navigate({ to: "/reports/$id", params: { id: reportId } });
        }}
        onEditReport={(reportId) => {
          void navigate({ to: "/reports/$id/edit", params: { id: reportId } });
        }}
        onOpenAsReport={(derived) => {
          void navigate({
            to: "/reports/new",
            state: {
              reportSeed: { query: derived, visualization: { schema_version: 1, kind: "table" } },
            },
          });
        }}
      />
    </div>
  );
}
